import { useEffect, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, Zap, Building2, Users, Package, Save, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Super-admin plan management panel.
 * - Lists all tenants with their current plan + usage vs quota
 * - Assign plan via dropdown
 * - Per-feature overrides toggle (grandfather clause)
 */
export default function TenantPlansSection() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [featureCatalog, setFeatureCatalog] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [busy, setBusy] = useState(false);
  const [expandedTenant, setExpandedTenant] = useState(null);
  const [overrides, setOverrides] = useState({});

  const load = async () => {
    try {
      const [pRes, tRes] = await Promise.all([api.get("/plans"), api.get("/tenants")]);
      setPlans(pRes.data.plans || []);
      setFeatureCatalog(pRes.data.features || []);
      setTenants(tRes.data || []);
    } catch (e) { toast.error("Failed to load plans / tenants"); }
  };
  useEffect(() => { if (user?.role === "super_admin") load(); /* eslint-disable-next-line */ }, [user]);

  const changePlan = async (tenantId, planId) => {
    setBusy(true);
    try {
      await api.patch(`/tenants/${tenantId}/plan`, { plan_id: planId });
      toast.success("Plan updated");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const openOverrides = (t) => {
    setExpandedTenant(t.id);
    setOverrides({ ...(t.feature_overrides || {}) });
  };

  const saveOverrides = async (tenantId) => {
    setBusy(true);
    try {
      await api.patch(`/tenants/${tenantId}/features`, { overrides });
      toast.success("Feature overrides saved");
      setExpandedTenant(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (user?.role !== "super_admin") return null;

  const planPill = (planId) => {
    const p = plans.find(x => x.id === planId);
    if (!p) return null;
    const tone = p.tier >= 3 ? "bg-primary text-primary-foreground" : p.tier === 2 ? "bg-amber-100 text-amber-800 border border-amber-300" : "bg-slate-100 text-slate-700 border border-slate-300";
    return <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${tone}`}>{p.name}</span>;
  };

  return (
    <section className="border border-primary/40 rounded-md bg-red-50/40 p-6 card-elev" data-testid="tenant-plans-section">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
        <div>
          <div className="overline flex items-center gap-2 text-primary"><Sparkles size={12} /> Platform · Super admin only</div>
          <h2 className="font-display text-xl mt-1">Tenant plans &amp; feature gating</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Assign Basic / Standard / Premium plans per tenant. Optionally grandfather individual features on top.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2 text-[10px]">
          {plans.map(p => (
            <div key={p.id} className="border border-border rounded-md p-2 bg-white min-w-[130px]" data-testid={`plan-card-${p.id}`}>
              <div className="flex items-center gap-1.5 font-bold">{p.tier === 3 && <Zap size={11} className="text-primary" />}{p.name}</div>
              <div className="mt-1 text-muted-foreground">{p.features.length} features · {p.quotas.branches}/{p.quotas.users}/{p.quotas.parcels_per_month}</div>
              <div className="text-[9px] text-muted-foreground mt-0.5 uppercase tracking-widest">br / users / parcels</div>
            </div>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto border border-border rounded-md bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border overline text-left bg-muted/30">
              <th className="p-3">Tenant</th>
              <th className="p-3">Plan</th>
              <th className="p-3">Change plan</th>
              <th className="p-3"><Building2 size={11} className="inline" /> Branches</th>
              <th className="p-3"><Users size={11} className="inline" /> Users</th>
              <th className="p-3"><Package size={11} className="inline" /> Parcels</th>
              <th className="p-3">Overrides</th>
            </tr>
          </thead>
          <tbody data-testid="tenant-plans-table">
            {tenants.map(t => {
              const q = t.quotas || {};
              const overCount = Object.keys(t.feature_overrides || {}).length;
              return (
                <>
                  <tr key={t.id} className="border-b border-border">
                    <td className="p-3 font-semibold">
                      <div>{t.name}</div>
                      <div className="text-[10px] text-muted-foreground mono">{t.code}</div>
                    </td>
                    <td className="p-3">{planPill(t.plan_id || "basic")}</td>
                    <td className="p-3">
                      <select data-testid={`plan-select-${t.code}`}
                        value={t.plan_id || "basic"}
                        onChange={(e) => changePlan(t.id, e.target.value)}
                        disabled={busy}
                        className="h-8 px-2 rounded-md border border-input bg-background text-xs">
                        {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </td>
                    <td className="p-3 mono text-xs">{t.branch_count ?? 0} / {q.branches === 999999 ? "∞" : q.branches}</td>
                    <td className="p-3 mono text-xs">{t.user_count ?? 0} / {q.users === 999999 ? "∞" : q.users}</td>
                    <td className="p-3 mono text-xs">{t.parcel_count ?? 0} / {q.parcels_per_month === 999999 ? "∞" : q.parcels_per_month}</td>
                    <td className="p-3">
                      <button data-testid={`plan-overrides-${t.code}`} onClick={() => expandedTenant === t.id ? setExpandedTenant(null) : openOverrides(t)}
                        className="text-[10px] font-semibold uppercase tracking-widest border border-border rounded px-2 py-1 hover:border-primary hover:text-primary transition-colors">
                        {overCount > 0 ? `${overCount} overrides` : "Set overrides"}
                        <ChevronDown size={10} className={`inline ml-1 transition-transform ${expandedTenant === t.id ? "rotate-180" : ""}`} />
                      </button>
                    </td>
                  </tr>
                  {expandedTenant === t.id && (
                    <tr className="border-b border-border bg-muted/20">
                      <td colSpan={7} className="p-4">
                        <div className="text-xs text-muted-foreground mb-2">
                          Force-enable or force-disable individual features (grandfather clauses). Empty = inherit from plan.
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                          {featureCatalog.map(f => {
                            const state = overrides[f];
                            return (
                              <div key={f} className="border border-border rounded p-2 bg-white text-xs flex items-center justify-between gap-2">
                                <span className="mono truncate">{f}</span>
                                <div className="flex items-center gap-0.5 shrink-0">
                                  <button data-testid={`override-${f}-on`} onClick={() => setOverrides(o => ({ ...o, [f]: true }))}
                                    className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${state === true ? "bg-emerald-500 text-white" : "border border-border text-muted-foreground"}`}>ON</button>
                                  <button data-testid={`override-${f}-off`} onClick={() => setOverrides(o => ({ ...o, [f]: false }))}
                                    className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${state === false ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground"}`}>OFF</button>
                                  <button data-testid={`override-${f}-inherit`} onClick={() => setOverrides(o => { const n = { ...o }; delete n[f]; return n; })}
                                    className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${state === undefined ? "bg-slate-300 text-slate-700" : "border border-border text-muted-foreground"}`}>—</button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="mt-3 flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setExpandedTenant(null)} className="rounded-md" size="sm">Cancel</Button>
                          <Button onClick={() => saveOverrides(t.id)} disabled={busy} className="rounded-md bg-primary text-primary-foreground" size="sm" data-testid={`save-overrides-${t.code}`}>
                            <Save size={12} className="mr-1" /> Save overrides
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
            {tenants.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-sm text-muted-foreground">No tenants yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
